import { trackEvent } from '../../../../config/analytics';
import { LockedAxies } from '../component/flow-item/shift/flow-item-shift';

interface TrackSelectionOptions {
  method: 'actionBar' | 'item';
  count: number;
  totalSelected: number;
  flowLength: number;
  type?: string;
}

class FlowAnalyticService {
  constructor(private eventNamePrefix: string) {}

  public trackMultiEdit(withPartDefaultsUpdate = false) {
    trackEvent({
      eventName: `${this.eventNamePrefix} -  Multi-edit`,
      forwardToIntercom: true,
      payload: {
        withPartDefaultsUpdate,
      },
    });
  }

  public trackMoveToPoint() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Move to Point`,
    });
  }

  public trackTouchUpPointClicked() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Touch Up Clicked`,
      forwardToIntercom: true,
    });
  }

  public trackFineTune(
    property:
      | 'travelAngle'
      | 'workAngle'
      | 'path'
      | 'stickout'
      | 'partOffset'
      | 'partPosition'
  ) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Fine Tune`,
      payload: {
        property,
      },
    });
  }

  public trackStartFromHere(longPress?: boolean) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Start From Here`,
      forwardToIntercom: !longPress,
      payload: {
        longPress,
      },
    });
  }

  public trackEnableStepMode(source: 'startFromHere') {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Step Mode Enabled`,
      payload: {
        source,
      },
    });
  }

  public trackCopyFlowItems() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Copy Flow Items`,
    });
  }

  public trackPasteFlowItems() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Paste Flow Items`,
    });
  }

  public trackSelectAllItems(flowLength: number) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Select All Items`,
      payload: {
        flowLength,
      },
    });
  }

  public trackUnselectAllItems(flowLength: number) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Unselect All Items`,
      payload: {
        flowLength,
      },
    });
  }

  public trackSelectItems(options: TrackSelectionOptions) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Select Items`,
      payload: {
        ...options,
      },
    });
  }

  public trackUnselectItems(options: TrackSelectionOptions) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Unselect Items`,
      payload: {
        ...options,
      },
    });
  }

  public trackLaunchedBucketExplorer() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Launched bucket explorer`,
    });
  }

  public trackLaunchedBucketWriter() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Launched bucket writer`,
    });
  }

  public trackAppliedBucketSettingsToPartSettings() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Applied bucket settings to part settings`,
    });
  }

  public trackAppliedSettingsToPart(hasLibrary?: boolean) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Applied settings to part`,
      payload: {
        hasLibrary: hasLibrary ?? false,
      },
      forwardToIntercom: !hasLibrary,
    });
  }

  public trackAppliedSettingsToPartAndUpdated() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Applied settings to part and updated library`,
    });
  }

  public trackAppliedSettingsToPartAndCreated() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Applied settings to part and created library`,
    });
  }

  public trackHideLibraryNotes() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Hiding library notes`,
    });
  }

  public trackMultipassCopy() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Copy Multipass`,
    });
  }

  public trackMultipassDelete(options: {
    scratchpadId: string;
    sectionItemId: string;
    originItemId: string;
  }) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Delete Multipass`,
      payload: {
        ...options,
      },
    });
  }

  public trackMultipassUndo(options: {
    scratchpadId: string;
    sectionItemId: string;
    originItemId: string;
  }) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Undo Multipass`,
      payload: {
        ...options,
      },
    });
  }

  public trackPresentedUnsavedItems(discarded = false) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Presented Unsaved Items`,
      payload: {
        discarded,
      },
    });
  }

  public trackResetPartSettings() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Reset Part Settings`,
    });
  }

  public trackPartSettingsSaveAsDefault(saveAsDefault = false) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Part Settings Save As Default`,
      payload: {
        saveAsDefault,
      },
    });
  }

  public trackSectionAdd() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Section Add`,
    });
  }

  public trackSectionRemove() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Section Remove`,
    });
  }

  public trackSectionRemoveAll() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Section Remove All`,
    });
  }

  public trackSectionCopyAll() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Section Copy All`,
    });
  }

  public trackSectionCollapse() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Section Collapse`,
    });
  }

  public trackSectionExpand() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Section Expand`,
    });
  }

  public trackSectionCollapseAll() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Section Collapse All`,
    });
  }

  public trackSectionExpandAll() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Section Expand All`,
    });
  }

  public trackSectionJumpTo() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Section Jump To`,
    });
  }

  public trackReorderFlowItem() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - ReorderFlowItem`,
    });
  }

  public trackReorderFlowItemStitch(options: {
    isParent: boolean;
    toParentId?: string;
    fromParentId?: string;
  }) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - ReorderFlowItem Stitch`,
      payload: {
        ...options,
      },
    });
  }

  public trackShiftInitiated() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Shift Initiation Action`,
    });
  }

  public trackShiftExpandedAxisDetail() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Shift Expanded Axis Detail`,
    });
  }

  public trackShiftLockedAxis(options: { lockedAxis: LockedAxies }) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Shift Locked Axis`,
      payload: {
        ...options,
      },
    });
  }

  public trackShiftInPlace(options: {
    lockedAxies: LockedAxies;
    numOfItems: number;
    isOriginInitialItem: boolean;
  }) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Shift In Place`,
      payload: {
        ...options,
      },
    });
  }

  public trackShiftAndCopy(options: {
    lockedAxies: LockedAxies;
    numOfItems: number;
    isOriginInitialItem: boolean;
  }) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Shift and Copy`,
      payload: {
        ...options,
      },
    });
  }

  public trackShiftCapturedAirMove() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Shift Captured Air Move`,
    });
  }

  public trackStitchConversion(options: {
    isEditing: boolean;
    scratchpadId: string;
    sectionItemId: string;
    originItemId: string;
    settings: Record<string, unknown>;
  }) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Convert to Stitch`,
      payload: {
        ...options,
      },
    });
  }

  public trackStitchCopy() {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Copy Stitch`,
    });
  }

  public trackStitchDelete(options: {
    scratchpadId: string;
    sectionItemId: string;
    originItemId: string;
  }) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Delete Stitch`,
      payload: {
        ...options,
      },
    });
  }

  public trackStitchUndo(options: {
    scratchpadId: string;
    sectionItemId: string;
    originItemId: string;
  }) {
    trackEvent({
      eventName: `${this.eventNamePrefix} - Undo Stitch`,
      payload: {
        ...options,
      },
    });
  }
}

export { FlowAnalyticService };
